// src/crm/ugc/format.ts
//
// Formats de l'admin du programme UGC, en heure de Paris : date courte (« 10 nov. 2026 »), date et
// heure d'un message (« 10 nov. 2026, 14 h 30 »), ancienneté (« il y a 5 h »), temps restant sur
// les 48 h d'une idée et taille d'un fichier ; libellés des états d'une vidéo et d'un membre vus
// par l'admin.

import type { MemberStatus, VideoStatus } from '../../lib/ugcTypes'

const DATE = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris' })
const DAY_MONTH = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', timeZone: 'Europe/Paris' })
const PARIS_DAY = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Paris' })
const PARIS_TIME = new Intl.DateTimeFormat('fr-FR', { hour: 'numeric', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Paris' })

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const MIB = 1024 * 1024

/** Horodatage ISO ou date AAAA-MM-JJ (lue à midi, pour rester le même jour) → « 10 nov. 2026 ». */
export function formatDate(value: string): string {
  return DATE.format(new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00.000Z` : value))
}

/** Nombre de jours du calendrier de Paris entre deux instants (`to` − `from`). */
function parisDayGap(from: Date, to: Date): number {
  return Math.round((Date.parse(PARIS_DAY.format(to)) - Date.parse(PARIS_DAY.format(from))) / (24 * HOUR))
}

/** Heure de Paris : « 14 h », « 14 h 30 ». */
function parisTime(date: Date): string {
  const parts = PARIS_TIME.formatToParts(date)
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0')
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00'
  return minute === '00' ? `${hour} h` : `${hour} h ${minute}`
}

/** Horodatage ISO → « 10 nov. 2026, 14 h 30 » (heure de Paris), pour un message du fil. */
export function formatDateTime(iso: string): string {
  return `${formatDate(iso)}, ${parisTime(new Date(iso))}`
}

/** Durée arrondie vers le bas : « 40 min », « 43 h ». */
function duration(ms: number): string {
  return ms < HOUR ? `${Math.max(1, Math.floor(ms / MINUTE))} min` : `${Math.floor(ms / HOUR)} h`
}

/** Ancienneté : « à l'instant », « il y a 25 min », « il y a 5 h », « hier », « il y a 4 jours ». */
export function formatAgo(iso: string, now: Date): string {
  const date = new Date(iso)
  const ms = now.getTime() - date.getTime()
  if (ms < MINUTE) return "à l'instant"
  if (ms < HOUR) return `il y a ${Math.floor(ms / MINUTE)} min`
  if (ms < 24 * HOUR) return `il y a ${Math.floor(ms / HOUR)} h`
  const days = parisDayGap(date, now)
  if (days <= 1) return 'hier'
  if (days <= 30) return `il y a ${days} jours`
  return `le ${formatDate(iso)}`
}

/**
 * Temps restant sur les 48 h promises à une idée (`deadline` ISO) : « reste 43 h, réponse avant
 * demain 14 h », puis « délai de 48 h dépassé de 3 h ».
 */
export function formatDeadline(deadline: string, now: Date): string {
  const date = new Date(deadline)
  const ms = date.getTime() - now.getTime()
  if (ms <= 0) return `délai de 48 h dépassé de ${duration(-ms)}`
  const days = parisDayGap(now, date)
  const when =
    days === 0 ? parisTime(date) : days === 1 ? `demain ${parisTime(date)}` : `le ${DAY_MONTH.format(date)}, ${parisTime(date)}`
  return `reste ${duration(ms)}, réponse avant ${when}`
}

/** Taille en Mo (arrondie, 1 au moins), comme la limite de 500 Mo du portail. */
export function formatSize(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / MIB))} Mo`
}

export const VIDEO_STATUS_LABELS: Record<VideoStatus, string> = {
  idea_submitted: 'Idée à relire',
  idea_changes_requested: 'Idée en correction chez la personne',
  idea_approved: 'Idée validée, brouillon attendu',
  draft_submitted: 'Brouillon à relire',
  draft_changes_requested: 'Brouillon en correction chez la personne',
  approved: 'Validée',
  rejected: 'Refusée',
}

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  invited: 'Invité',
  active: 'Actif',
  paused: 'En pause',
  left: 'Parti',
}
